import * as THREE from 'three';
import { blackbodyRgb } from './radiatorGlow';

/**
 * An authored drive's hardware heat: the throat (`drive-glow`) lit while the
 * drive burns, and the bells' skin and lip (`drive-heat`) soaking heat over a
 * long burn and shedding it after cut-off, brightest at the exit and fading
 * upstream. Cosmetic; nothing in a fight reads it.
 *
 * It runs on the dt custos.update is handed, which is sim seconds (step seconds
 * in a fight): heat soak is physical, so under time warp the bells come up to
 * temperature and cool in sim time. The plume's ignition and cut-off
 * (thrusters.ts) are on real time on purpose.
 */

/** Seconds the throat takes to follow the burn, up and down. */
const THROAT_TAU_S = { up: 0.15, down: 0.7 };
/** The throat's emissive intensity idle (a DFD keeps its reactor lit for ship power) and at full burn. */
const THROAT_IDLE = 0.15;
const THROAT_FULL = 1.5;
/** What the throat whitens to as it heats, linear. */
const THROAT_HOT = new THREE.Color().setRGB(0.78, 0.86, 1.0, THREE.LinearSRGBColorSpace);
/**
 * The lip's temperature cold and under a sustained full burn, and how fast it
 * gets there: at full throttle it shows after about three seconds and peaks by
 * fifteen, and it is dark again ten seconds after cut-off. Below 0.6 throttle
 * it never shows at all.
 */
const LIP_COLD_K = 300;
const LIP_FULL_K = 1450;
const LIP_TAU_S = { up: 6, down: 10 };
const LIP_SHOW_K = 750;
const LIP_GAIN = 1.1;
/** How far upstream of the exit plane the skin's glow falls to 1/e, in exit radii. */
const LIP_FALLOFF = 0.7;
const MAX_EXITS = 4;

// What the exit pulls are computed from, shared by the skin and the soot.
const EXIT_DECL = `uniform vec4 uDhExit[ ${MAX_EXITS} ];
uniform vec3 uDhDir[ ${MAX_EXITS} ];
uniform float uDhGrit;
varying vec3 vDhW;
varying vec3 vDhO;
float dhHash( vec3 p ) { p = fract( p * 0.3183099 + 0.1 ); p *= 17.0; return fract( p.x * p.y * p.z * ( p.x + p.y + p.z ) ); }
float dhNoise( vec3 x ) {
	vec3 i = floor( x ); vec3 f = fract( x ); f = f * f * ( 3.0 - 2.0 * f );
	return mix( mix( mix( dhHash( i ), dhHash( i + vec3( 1, 0, 0 ) ), f.x ), mix( dhHash( i + vec3( 0, 1, 0 ) ), dhHash( i + vec3( 1, 1, 0 ) ), f.x ), f.y ),
		mix( mix( dhHash( i + vec3( 0, 0, 1 ) ), dhHash( i + vec3( 1, 0, 1 ) ), f.x ), mix( dhHash( i + vec3( 0, 1, 1 ) ), dhHash( i + vec3( 1, 1, 1 ) ), f.x ), f.y ), f.z );
}`;

function patchVertex(vs: string): string {
  return vs
    .replace('#include <common>', `#include <common>
varying vec3 vDhW;
varying vec3 vDhO;`)
    .replace('#include <begin_vertex>', `#include <begin_vertex>
{
	vec4 dhP = vec4( transformed, 1.0 );
#ifdef USE_INSTANCING
	dhP = instanceMatrix * dhP;
#endif
	vDhO = dhP.xyz;
	vDhW = ( modelMatrix * dhP ).xyz;
}`);
}

/** Bell steel that has run hot, linear: blue-grey at the exit, bronze, then straw upstream. */
const TINT_EXIT = '0.035, 0.042, 0.07';
const TINT_MID = '0.11, 0.066, 0.03';
const TINT_UP = '0.16, 0.12, 0.06';

export interface DriveHeatConfig {
  throat?: THREE.MeshStandardMaterial;
  skin?: THREE.MeshStandardMaterial;
  /** The throat's colour cold: the livery's plume colour. */
  coolColor: number;
  /** Service weathering (custos.ts `grit`): heat tint on the skin, soot on `soot`. 0 is none. */
  grit?: number;
  /** The hull's own materials round the drive, sooted near each exit when `grit` > 0. */
  soot?: THREE.MeshStandardMaterial[];
}

export interface DriveHeatRead {
  /** The throat's heat, [0, 1], and the emissive intensity it drew. */
  throat: number;
  throatIntensity: number;
  /** The lip's temperature, kelvin, and its glow over full, [0, 1]. */
  lipK: number;
  lipGlow: number;
  exits: number;
}

export interface DriveHeat {
  /**
   * The hull's own drive needles (custos.ts `drive-plume`): each one's origin
   * is a nozzle exit and its -Y the way it vents.
   */
  setExits: (needles: THREE.Mesh[]) => void;
  update: (throttle: number, dt: number) => void;
  read: () => DriveHeatRead;
}

/** Patches `skin` and `soot` in place, so call it before anything that chains onto their onBeforeCompile. */
export function createDriveHeat(config: DriveHeatConfig): DriveHeat {
  const { throat: throatMat, skin } = config;
  const grit = config.grit ?? 0;
  const sootMats = grit > 0 ? config.soot ?? [] : [];
  const cool = new THREE.Color(config.coolColor);
  const uniforms = {
    uDhExit: { value: Array.from({ length: MAX_EXITS }, () => new THREE.Vector4()) },
    uDhDir: { value: Array.from({ length: MAX_EXITS }, () => new THREE.Vector3(0, 0, 1)) },
    uDhHot: { value: new THREE.Vector3() },
    uDhCool: { value: new THREE.Vector3() },
    uDhFall: { value: LIP_FALLOFF },
    uDhGrit: { value: grit },
  };
  if (skin) {
    const prev = skin.onBeforeCompile.bind(skin);
    const prevKey = skin.customProgramCacheKey();
    skin.onBeforeCompile = (shader, renderer) => {
      prev(shader, renderer);
      Object.assign(shader.uniforms, uniforms);
      shader.vertexShader = patchVertex(shader.vertexShader);
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', `#include <common>
${EXIT_DECL}
uniform vec3 uDhHot;
uniform vec3 uDhCool;
uniform float uDhFall;`)
        .replace('#include <lights_physical_fragment>', `{
	// the nearest exit's pull: 1 at its plane, falling off upstream, nothing
	// off its own bell
	float dhW = 0.0;
	float dhA = 0.0;
	for ( int i = 0; i < ${MAX_EXITS}; i ++ ) {
		vec4 e = uDhExit[ i ];
		if ( e.w <= 0.0 ) continue;
		vec3 d = vDhW - e.xyz;
		float ax = - dot( d, uDhDir[ i ] );
		float rr = length( d + uDhDir[ i ] * ax );
		float w = exp( - max( ax, 0.0 ) / ( uDhFall * e.w ) ) * ( 1.0 - smoothstep( 1.3 * e.w, 1.8 * e.w, rr ) );
		if ( w > dhW ) { dhW = w; dhA = max( ax, 0.0 ) / e.w; }
	}
	if ( uDhGrit > 0.0 && dhW > 0.0 ) {
		float n = dhNoise( vDhO * 9.0 ) - 0.5;
		float a = dhA + 0.6 * n;
		vec3 tint = mix( vec3( ${TINT_EXIT} ), vec3( ${TINT_MID} ), smoothstep( 0.3, 1.2, a ) );
		tint = mix( tint, vec3( ${TINT_UP} ), smoothstep( 1.2, 2.2, a ) );
		diffuseColor.rgb = mix( diffuseColor.rgb, tint, min( uDhGrit, 1.0 ) * 0.8 * ( 1.0 - smoothstep( 2.2, 3.2, a ) ) );
	}
	totalEmissiveRadiance += mix( uDhCool, uDhHot, dhW ) * dhW;
}
#include <lights_physical_fragment>`);
    };
    skin.customProgramCacheKey = () => `${prevKey}|driveheat-v2`;
    skin.needsUpdate = true;
  }
  // soot settles on the plate round each exit and thins upstream, dull and dark
  for (const mat of sootMats) {
    const prev = mat.onBeforeCompile.bind(mat);
    const prevKey = mat.customProgramCacheKey();
    mat.onBeforeCompile = (shader, renderer) => {
      prev(shader, renderer);
      Object.assign(shader.uniforms, uniforms);
      shader.vertexShader = patchVertex(shader.vertexShader);
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', `#include <common>
${EXIT_DECL}`)
        .replace('#include <lights_physical_fragment>', `{
	float dhS = 0.0;
	for ( int i = 0; i < ${MAX_EXITS}; i ++ ) {
		vec4 e = uDhExit[ i ];
		if ( e.w <= 0.0 ) continue;
		vec3 d = vDhW - e.xyz;
		float ax = - dot( d, uDhDir[ i ] );
		float rr = length( d + uDhDir[ i ] * ax );
		float s = exp( - max( ax, 0.0 ) / ( 2.4 * e.w ) ) * ( 1.0 - smoothstep( 2.0 * e.w, 4.5 * e.w, rr ) )
			* smoothstep( - 0.6 * e.w, 0.0, ax );
		dhS = max( dhS, s );
	}
	if ( dhS > 0.0 ) {
		dhS = clamp( dhS * uDhGrit * ( 0.45 + 0.55 * dhNoise( vDhO * 7.0 ) ), 0.0, 1.0 );
		diffuseColor.rgb *= 1.0 - 0.8 * dhS;
		roughnessFactor = mix( roughnessFactor, 1.0, 0.6 * dhS );
		metalnessFactor *= 1.0 - 0.7 * dhS;
	}
}
#include <lights_physical_fragment>`);
    };
    mat.customProgramCacheKey = () => `${prevKey}|dhsoot-v2`;
    mat.needsUpdate = true;
  }

  let exits: THREE.Mesh[] = [];
  let throat = 0;
  let throatIntensity = THROAT_IDLE;
  let lipK = LIP_COLD_K;
  let lipGlow = 0;
  let first = true;
  let glowOut = true;
  const _p = new THREE.Vector3();
  const _q = new THREE.Quaternion();
  const _s = new THREE.Vector3();

  const toward = (from: number, to: number, dt: number, tau: { up: number; down: number }): number =>
    from + (to - from) * (1 - Math.exp(-dt / (to > from ? tau.up : tau.down)));

  function update(throttle: number, dt: number): void {
    const lv = Math.min(Math.max(throttle, 0), 1);
    const lipTarget = LIP_COLD_K + (LIP_FULL_K - LIP_COLD_K) * Math.pow(lv, 1.5);
    if (first) {
      first = false;
      throat = lv;
    } else if (dt > 0) {
      throat = toward(throat, lv, dt, THROAT_TAU_S);
      lipK = toward(lipK, lipTarget, dt, LIP_TAU_S);
    }
    if (throatMat) {
      throatIntensity = THROAT_IDLE + (THROAT_FULL - THROAT_IDLE) * Math.pow(throat, 1.2);
      throatMat.emissive.copy(cool).lerp(THROAT_HOT, throat * throat);
      throatMat.emissiveIntensity = throatIntensity;
    }
    if (skin || sootMats.length) placeExits();
    if (!skin) return;
    const t = THREE.MathUtils.smoothstep(lipK, LIP_SHOW_K, LIP_FULL_K);
    lipGlow = Math.pow(t, 1.5);
    // Below LIP_SHOW_K the skin adds no glow, so a cold drive skips both Planck
    // integrals; the exits are still placed above, since the soot and tint read them.
    if (lipGlow === 0) {
      if (!glowOut) {
        uniforms.uDhHot.value.set(0, 0, 0);
        uniforms.uDhCool.value.set(0, 0, 0);
        glowOut = true;
      }
      return;
    }
    glowOut = false;
    const [hr, hg, hb] = blackbodyRgb(lipK);
    uniforms.uDhHot.value.set(hr, hg, hb).multiplyScalar(LIP_GAIN * lipGlow);
    // upstream of the lip the steel runs cooler: redder, and dimmer again
    const [cr, cg, cb] = blackbodyRgb(Math.max(0.82 * lipK, LIP_COLD_K));
    uniforms.uDhCool.value.set(cr, cg, cb).multiplyScalar(0.5 * LIP_GAIN * lipGlow * t);
  }

  function placeExits(): void {
    for (let i = 0; i < MAX_EXITS; i++) {
      const needle = exits[i];
      const e = uniforms.uDhExit.value[i]!;
      if (!needle) { e.w = 0; continue; }
      needle.getWorldPosition(_p);
      needle.getWorldQuaternion(_q);
      needle.getWorldScale(_s);
      const cone = (needle.geometry as THREE.ConeGeometry).parameters?.radius ?? 0.16;
      e.set(_p.x, _p.y, _p.z, cone * Math.abs(_s.x));
      uniforms.uDhDir.value[i]!.set(0, -1, 0).applyQuaternion(_q).normalize();
    }
  }

  return {
    setExits: (needles) => { exits = needles.slice(0, MAX_EXITS); },
    update,
    read: () => ({ throat, throatIntensity, lipK, lipGlow, exits: exits.length }),
  };
}

